import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { environment } from "../../../../environment";
import { catchError } from "rxjs/operators";
import { InternalCommunicationMessage } from "../interfaces/home/internal-communication-message.interface";
import { map } from "rxjs/operators";
import { SubAcuteManagementDetails } from "../interfaces/home/subacute-management-details.interface";
import { UserJobTitle } from "./../interfaces/home/user-job-title.interface";
import { LongTermCareManagementDetails } from "../interfaces/home/long-term-care-management.interface";
import { CensusDetails } from "../interfaces/home/census.interface";
import { ScheduleDetails } from "../interfaces/home/schedule-details.interface";
import { KPIDetails } from "./../interfaces/home/KPI-details.interface";
import { ApplyTaskFilters, Tasks } from "../interfaces/home/tasks.interface";
import { BillingTiltDetails } from "../interfaces/home/billingTile-details.interface";
import { AppConstants } from "../constants/app-constants";

@Injectable({
  providedIn: "root",
})
export class LandingClientService {
  private readonly TileEndpoint = {
    InternalCommunication: "/internal-Communication",
    Tasks: "/tasks",
    TaskDetails: "/task-details",
    KPI: "/get-kpi-details",
    Census: "/census",
    SubacuteManagement: "/subacute-management",
    LongTermCareManagement: "/long-term-care-management",
    Schedule: "/schedule",
    Efficiency: "/efficiency",
    Billing: "/billing",
    BillingTile: "/billing-tile",
    Alerts: "/alerts",
    Productivity: "/productivity",
    JobTitleDetails: "/job-title-details",
  };

  private readonly baseEndpoint = `${environment.endpoint}/dashboard`;

  constructor(private httpClient: HttpClient) {}

  updateMessage(
    message: InternalCommunicationMessage
  ): Observable<InternalCommunicationMessage> {
    return this.httpClient
      .post<InternalCommunicationMessage>(
        `${this.baseEndpoint}${this.TileEndpoint.InternalCommunication}`,
        message
      )
      .pipe(
        catchError((error) => {
          console.error("Error updating message:", error);
          return of(message);
        })
      );
  }

  getMessages(facilityId: string): Observable<InternalCommunicationMessage[]> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.InternalCommunication}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: InternalCommunicationMessage[] }>(url)
      .pipe(map((response) => response.responseData || []));
  }

  addMessage(
    message: InternalCommunicationMessage
  ): Observable<InternalCommunicationMessage> {
    return this.httpClient
      .post<InternalCommunicationMessage>(
        `${this.baseEndpoint}${this.TileEndpoint.InternalCommunication}`,
        message
      )
      .pipe(
        catchError((error) => {
          console.error("Error adding message:", error);
          return of({ ...message });
        })
      );
  }

  deleteMessage(message: InternalCommunicationMessage): Observable<void> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.InternalCommunication}/delete/${message.id}`;
    return this.httpClient.post<void>(url, message).pipe(
      catchError((error) => {
        console.error("Error deleting message:", error);
        return of();
      })
    );
  }

  saveAsDraft(
    message: InternalCommunicationMessage
  ): Observable<InternalCommunicationMessage> {
    message.savedAsDraft = true;
    return this.addMessage(message);
  }

  getSubacuteManagementDetails(
    facilityId: string
  ): Observable<SubAcuteManagementDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.SubacuteManagement}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: SubAcuteManagementDetails }>(url)
      .pipe(map((response) => response.responseData));
  }

  getLongTermCareManagementDetails(
    facilityId: string
  ): Observable<LongTermCareManagementDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.LongTermCareManagement}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: LongTermCareManagementDetails }>(url)
      .pipe(map((response) => response.responseData || []));
  }

  getJobTitleDetails(): Observable<UserJobTitle> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.JobTitleDetails}`;
    return this.httpClient
      .get<{ responseData: UserJobTitle }>(url)
      .pipe(map((response) => response.responseData));
  }

  getScheduleDetails(facilityId: string): Observable<ScheduleDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.Schedule}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: ScheduleDetails }>(url)
      .pipe(map((response) => response.responseData || []));
  }

  getBillingTileDetails(facilityId: string): Observable<BillingTiltDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.BillingTile}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: BillingTiltDetails }>(url)
      .pipe(map((response) => response.responseData || []));
  }

  getCensusDetails(facilityId: string): Observable<CensusDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.Census}/facility/${facilityId}`;
    return this.httpClient
      .get<{ responseData: CensusDetails }>(url)
      .pipe(map((response) => response.responseData || []));
  }

  getKPIDetails(facilityId: string, viewType : number): Observable<KPIDetails> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.KPI}`;
    const params = { facilityId, viewType: viewType.toString() };
    return this.httpClient
      .get<{ responseData: KPIDetails }>(url, { params })
      .pipe(map((response) => response.responseData || []));
  }

  getTasks(facilityId: string | null, viewType: number, skipSpinner: boolean = false): Observable<Tasks[]> {
    const url = `${this.baseEndpoint}${this.TileEndpoint.TaskDetails}`;
    const requestBody = { facilityId, viewType };

    let options = {};
    if (skipSpinner) {
      options = { 
        headers: { [AppConstants.skipSpinner]: 'true' }
      };
    }

    return this.httpClient
      .post<{ responseData: Tasks[] }>(url, requestBody, options)
      .pipe(map((response) => response.responseData || []));
  }

  getScheduledReport(facilityId: string): Observable<any> {
    const url = `${this.baseEndpoint}/get-schedule-report/facility/${facilityId}`;

    return this.httpClient.get(url, {
      responseType: "blob",
      observe: "response",
    });
  }

  getFilteredAlertData(taskFilters: ApplyTaskFilters): Observable<any> {
    const url = `${this.baseEndpoint}/taskfilter-details`;
    return this.httpClient.post<Tasks>(url, taskFilters);
  }
}
